import { parseDateOnly } from "@nimit9/signet-lib/format"
import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Badge } from "@nimit9/signet-ui/components/badge"
import { Button } from "@nimit9/signet-ui/components/button"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nimit9/signet-ui/components/table"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { type UpcomingPayment, useUpcoming } from "@/hooks/use-upcoming"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { daysUntil } from "@/lib/dates"
import {
  buildUpcomingHeadline,
  groupByWindow,
  isAmountUnknown,
  knownTotal,
  WINDOWS,
} from "./-headline"

export const Route = createFileRoute("/upcoming/")({
  component: Upcoming,
})

const TYPE_LABELS: Record<UpcomingPayment["type"], string> = {
  cc_bill: "CC Bill",
  emi: "EMI",
  fd: "FD Maturity",
  insurance: "Insurance",
  sip: "SIP",
  subscription: "Subscription",
}

/** "Mon, 28 Sept" for a date-only API string, read as a local date. */
function formatDay(dateStr: string) {
  return parseDateOnly(dateStr).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  })
}

function relativeDay(dateStr: string) {
  const days = daysUntil(dateStr)
  if (days < 0) return `${-days}d overdue`
  if (days === 0) return "Today"
  if (days === 1) return "Tomorrow"
  return `in ${days}d`
}

/** The per-type fact after the date: months left, platform, card, bank… */
function paymentMeta(payment: UpcomingPayment): string | null {
  switch (payment.type) {
    case "emi":
      return `${payment.meta.monthsLeft} months left`
    case "sip":
      return payment.meta.platform ?? null
    case "insurance":
      return payment.meta.provider ?? null
    case "cc_bill":
      return `${payment.meta.bankName} ····${payment.meta.last4}`
    case "subscription":
      return [payment.meta.frequency, payment.meta.category].filter(Boolean).join(" · ")
    case "fd":
      return `${payment.meta.bank} · ${payment.meta.interestRate}% p.a.`
    default:
      return null
  }
}

function PaymentAmount({ payment, className }: { payment: UpcomingPayment; className?: string }) {
  if (isAmountUnknown(payment)) {
    return <span className="text-text-muted">Amount unknown</span>
  }
  return <Amount value={payment.amount} className={className} />
}

const paymentKey = (p: UpcomingPayment) => `${p.type}-${p.name}-${p.date}`

/** Phone: stacked rows — badge + name + date line, amount right-aligned. */
function PaymentRow({ payment }: { payment: UpcomingPayment }) {
  const meta = paymentMeta(payment)
  return (
    <li
      data-testid="upcoming-row"
      className="flex min-w-0 items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <Badge variant="outline" className="shrink-0">
            {TYPE_LABELS[payment.type]}
          </Badge>
          <p className="truncate text-body font-medium text-text-primary">{payment.name}</p>
        </div>
        <p className="mt-1 text-caption text-text-muted">
          {formatDay(payment.date)}
          {meta && ` · ${meta}`}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-body font-medium text-text-primary">
          <PaymentAmount payment={payment} />
        </p>
        <p className="text-caption text-text-muted">{relativeDay(payment.date)}</p>
      </div>
    </li>
  )
}

/** Desktop: one table per window. */
function PaymentsTable({ items }: { items: UpcomingPayment[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="pl-4">Payment</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Details</TableHead>
          <TableHead>Due</TableHead>
          <TableHead className="pr-4 text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((p) => (
          <TableRow key={paymentKey(p)} data-testid="upcoming-table-row">
            <TableCell className="max-w-64 truncate pl-4 font-medium text-text-primary">
              {p.name}
            </TableCell>
            <TableCell>
              <Badge variant="outline">{TYPE_LABELS[p.type]}</Badge>
            </TableCell>
            <TableCell className="max-w-64 truncate text-text-secondary">
              {paymentMeta(p) ?? "—"}
            </TableCell>
            <TableCell className="text-text-secondary">
              {formatDay(p.date)}
              <span className="ml-1.5 text-caption text-text-muted">({relativeDay(p.date)})</span>
            </TableCell>
            <TableCell className="pr-4 text-right font-medium text-text-primary">
              <PaymentAmount payment={p} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function Upcoming() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useUpcoming()
  const payments = useMemo(() => data?.data ?? [], [data])
  const groups = useMemo(() => groupByWindow(payments), [payments])
  const headline = useMemo(() => buildUpcomingHeadline(payments), [payments])
  const totalAmount = knownTotal(payments)

  if (isLoading) {
    return (
      <>
        <Header title="Upcoming" showPeriod={false} />
        <PageWrapper>
          <div className="flex flex-col gap-4">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-40 w-full animate-pulse rounded-xl" />
            ))}
          </div>
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Upcoming" showPeriod={false} />
        <PageWrapper>
          <ErrorState
            message={error instanceof Error ? error.message : undefined}
            onRetry={refetch}
          />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <Header title="Upcoming Payments" showPeriod={false} />
      <PageWrapper>
        {payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
            <p className="text-callout font-medium text-text-primary">Nothing due in 60 days</p>
            <p className="mt-1 text-body text-text-muted">
              Add EMIs, SIPs, or insurance policies to track upcoming payments
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button
                size="touch"
                variant="outline"
                render={<Link to="/goals" />}
                nativeButton={false}
              >
                Add an EMI
              </Button>
              <Button
                size="touch"
                variant="outline"
                render={<Link to="/investments" />}
                nativeButton={false}
              >
                Add a SIP
              </Button>
              <Button
                size="touch"
                variant="outline"
                render={<Link to="/insurance" />}
                nativeButton={false}
              >
                Add a policy
              </Button>
            </div>
          </div>
        ) : (
          <div
            data-testid="upcoming-page"
            data-layout={isDesktop ? "desktop" : "phone"}
            className="flex min-w-0 flex-col gap-4 md:gap-5"
          >
            {headline && (
              <AnswerHeadline
                headline={toSignetHeadline(headline)}
                renderValue={renderAnswerAmount}
              />
            )}

            <StatStrip
              isDesktop={isDesktop}
              items={[
                { label: "Due in 60 days", value: <Amount value={totalAmount} /> },
                { label: "Payments", value: String(payments.length) },
                { label: "Due this week", value: String(groups["This week"].length) },
              ]}
            />

            {WINDOWS.map((group) => {
              const items = groups[group]
              if (items.length === 0) return null
              return (
                <section
                  key={group}
                  data-testid="upcoming-group"
                  className="flex min-w-0 flex-col gap-3 rounded-xl bg-bg-surface py-4 ring-1 ring-border-subtle"
                >
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4">
                    <h2 className="text-callout font-semibold text-text-primary">{group}</h2>
                    <span className="text-caption text-text-muted">
                      <Amount value={knownTotal(items)} /> · {items.length} payment
                      {items.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {isDesktop ? (
                    <div
                      className="overflow-x-auto"
                      role="region"
                      aria-label="Upcoming payments"
                      // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region must be reachable by keyboard
                      tabIndex={0}
                    >
                      <PaymentsTable items={items} />
                    </div>
                  ) : (
                    <ul className="divide-y divide-border-subtle px-4">
                      {items.map((p) => (
                        <PaymentRow key={paymentKey(p)} payment={p} />
                      ))}
                    </ul>
                  )}
                </section>
              )
            })}
          </div>
        )}
      </PageWrapper>
    </>
  )
}
